/* eslint-disable no-param-reassign */
import { types as t, Instance, flow, getParent, getIdentifier } from 'mobx-state-tree';
import api from '~/api';

// Tool execution status types - single source of truth
export const TOOL_EXECUTION_STATUSES = ['executing', 'completed', 'error', 'stopped', 'needs_clarification'] as const;
export type ToolExecutionStatus = (typeof TOOL_EXECUTION_STATUSES)[number];

// Tool Execution Model with proper MST actions
export const ToolExecutionModel = t
  .model('ToolExecutionModel', {
    currentTool: t.maybeNull(t.string),
    status: t.enumeration('ToolExecutionStatus', [...TOOL_EXECUTION_STATUSES]),
    description: t.maybeNull(t.string)
  })
  .actions((self) => ({
    updateStatus(status: ToolExecutionStatus) {
      self.status = status;
    },

    updateDescription(description: string | null) {
      self.description = description;
    },

    updateCurrentTool(tool: string | null) {
      self.currentTool = tool;
    },

    // Combined action for atomic updates
    update(data: { status?: ToolExecutionStatus; description?: string | null; currentTool?: string | null }) {
      if (data.status !== undefined) {
        self.status = data.status;
      }
      if (data.description !== undefined) {
        self.description = data.description;
      }
      if (data.currentTool !== undefined) {
        self.currentTool = data.currentTool;
      }
    }
  }));

// External file attached to a human message (rendered in the message bubble).
export const MessageAttachmentModel = t.model('MessageAttachment', {
  file_id: t.string,
  filename: t.string
});

// Message Model with SSE support and proper MST actions
export const MessageModel = t
  .model('MessageModel', {
    id: t.maybeNull(t.string),
    type: t.enumeration(['human', 'ai']),
    content: t.string,
    timestamp: t.string,
    run_id: t.maybeNull(t.string),
    feedback: t.maybeNull(t.enumeration(['good', 'bad'])),
    toolExecution: t.maybeNull(ToolExecutionModel),
    // `[null, undefined]` fallback: the server sends `attachments: null` for messages
    // without files, and MST's `t.optional` would otherwise reject null and throw on load.
    attachments: t.optional(t.array(MessageAttachmentModel), [], [null, undefined])
  })
  .actions((self) => ({
    setContent(newContent: string) {
      self.content = newContent;
    },

    setRunId(newRunId: string) {
      self.run_id = newRunId;
    },

    setToolExecution(
      toolData: {
        currentTool?: string | null;
        status: ToolExecutionStatus;
        description?: string | null;
      } | null
    ) {
      if (toolData === null) {
        self.toolExecution = null;
      } else {
        self.toolExecution = ToolExecutionModel.create(toolData);
      }
    },

    updateToolExecution(data: { status?: ToolExecutionStatus; description?: string | null; currentTool?: string | null }) {
      if (self.toolExecution) {
        self.toolExecution.update(data);
      }
    },

    sendFeedback: flow(function* sendFeedback(feedback) {
      const thread = getParent<any>(self, 2);
      yield api.noesis.sendFeedback({ runId: self.run_id!, feedback, threadId: getIdentifier(thread)!, messageId: self.id! });
      self.feedback = feedback;
    })
  }));

// Type exports
export interface IToolExecutionModel extends Instance<typeof ToolExecutionModel> {}
export interface IMessageModel extends Instance<typeof MessageModel> {}
